import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';

/**
 * Load/Delivery Date Cell Renderer Component
 *
 * Renders load and delivery dates in the format:
 * - C: DD-MM-YYYY (Carga/Load)
 * - E: DD-MM-YYYY (Entrega/Delivery)
 *
 * Copied from: src/app/modules/components/monitor/table-load-list/table-load-list.component.html (lines 316-324)
 * formatDate() method copied from: table-load-list.component.ts (lines 664-682)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'loadDateTime',
 *   cellRenderer: LoadDeliveryDateCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-load-delivery-date-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div>
      <b>C:</b> {{ formattedLoadDate }}<br />
      <b>E:</b> {{ formattedDeliveryDate }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadDeliveryDateCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Load date/time
   */
  get loadDateTime(): string | Date | null | undefined {
    return this.params?.data?.loadDateTime;
  }

  /**
   * Delivery date/time
   */
  get deliveryDateTime(): string | Date | null | undefined {
    return this.params?.data?.deliveryDateTime;
  }

  /**
   * Get formatted load date
   */
  get formattedLoadDate(): string {
    return this.formatDate(this.loadDateTime, false);
  }

  /**
   * Get formatted delivery date
   */
  get formattedDeliveryDate(): string {
    return this.formatDate(this.deliveryDateTime, false);
  }

  /**
   * Format date to DD-MM-YYYY format
   * Copied from original table-load-list.component.ts (lines 664-682)
   *
   * @param date - Date string or Date object
   * @param includeTime - Whether to include time in output
   * @returns Formatted date string
   */
  private formatDate(date: string | Date | null | undefined, includeTime?: boolean): string {
    if (date) {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();

      if (includeTime) {
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const seconds = d.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
      }
      return `${day}-${month}-${year}`;
    }
    return '--/--/----';
  }
}
